import { handleActions } from 'redux-actions';
import _without from 'lodash/without';
import _filter from 'lodash/filter';
import _isMatch from 'lodash/isMatch';
import { createSelector } from 'reselect';
import { getLogger } from '../logger/index';

const reduxUploadsMap = {};

const initialState = {
  interuptedWithNetworkError: false,
  uploadsCount: 0, // this counts the amount of uploads in the queue in the moment of starting the upload (needed to count overall progress)
  uploads: [],
};

// We use the same action types in the web app, the prefix X_ is added

export const uploadActionTypes = {
  UPLOAD_FILE_QUEUE: 'X_UPLOAD_FILE_QUEUE',
  UPLOAD_FILE_QUEUE_REMOVE: 'X_UPLOAD_FILE_QUEUE_REMOVE',
  UPLOAD_FILE_START: 'X_UPLOAD_FILE_START',
  UPLOAD_FILE_PROGRESS: 'X_UPLOAD_FILE_PROGRESS',
  UPLOAD_FILE_PROGRESS_WITH_UPLOAD_ID: 'X_UPLOAD_FILE_PROGRESS_WITH_UPLOAD_ID',
  UPLOAD_FILE_COMPLETE: 'X_UPLOAD_FILE_COMPLETE',
  UPLOAD_FILE_ERROR: 'X_UPLOAD_FILE_ERROR',
  UPLOAD_FILE_RETRY: 'X_UPLOAD_RETRY',
  UPLOAD_QUEUE_START: 'X_UPLOAD_QUEUE_START',
  UPLOAD_QUEUE_CLEAN: 'X_UPLOAD_QUEUE_CLEAN',
  UPLOAD_FILE_STOP: 'X_UPLOAD_FILE_STOP',
  UPLOAD_INTERUPTED_NETWORK_ERROR: 'X_UPLOAD_INTERUPTED_NETWORK_ERROR',
  UPLOAD_SET_ID: 'X_UPLOAD_SET_ID',
};

function createActions(options) {
  return {
    uploadActionTypes,
    addFileToUploadQueue: (file, data, context, size = 100, prepend = false) => ({
      type: uploadActionTypes.UPLOAD_FILE_QUEUE,
      payload: { file, data, size, options, context, prepend },
    }),
    removeFileFromUploadQueue: file => ({
      type: uploadActionTypes.UPLOAD_FILE_QUEUE_REMOVE,
      payload: { file },
    }),
    startFileUpload: (file, data, context, size = 100) => ({
      type: uploadActionTypes.UPLOAD_FILE_START,
      payload: { file, data, size, options, context },
    }),
    stopFileUpload: file => ({
      type: uploadActionTypes.UPLOAD_FILE_STOP,
      payload: { file },
    }),
    setNoConnectionForUpload: () => ({
      type: uploadActionTypes.UPLOAD_INTERUPTED_NETWORK_ERROR,
    }),
    setFileUploadProgress: (file, progress = 0) => ({
      type: uploadActionTypes.UPLOAD_FILE_PROGRESS,
      payload: { file, progress, options },
    }),
    setFileUploadProgressWithUploadId: (uploadId, progress = 0) => ({
      type: uploadActionTypes.UPLOAD_FILE_PROGRESS_WITH_UPLOAD_ID,
      payload: { uploadId, progress, options },
    }),
    setFileUploadComplete: (file, context, uploadId = null) => ({
      type: uploadActionTypes.UPLOAD_FILE_COMPLETE,
      payload: { file, options, context, uploadId },
    }),
    setFileUploadFailure: (file, error) => ({
      type: uploadActionTypes.UPLOAD_FILE_ERROR,
      payload: { file, error, options },
      error: true,
    }),
    retryFileUpload: (file, data, size) => ({
      type: uploadActionTypes.UPLOAD_FILE_RETRY,
      payload: { file, data, size, options },
    }),
    startUploadFromQueue: () => ({
      type: uploadActionTypes.UPLOAD_QUEUE_START,
    }),
    cleanUploadQueue: ({ cleanFailedUploads, cleanCancelledUploads }) => ({
      type: uploadActionTypes.UPLOAD_QUEUE_CLEAN,
      payload: { cleanFailedUploads, cleanCancelledUploads },
    }),
    setUploadId: (file, uploadId) => ({
      type: uploadActionTypes.UPLOAD_SET_ID,
      payload: { file, uploadId },
    }),
  };
}

export function createUploadReducer() {
  const reducers = {
    [uploadActionTypes.UPLOAD_FILE_QUEUE]: (state, { payload: { file, data, size, options, context, prepend } }) => {
      const newUpload = createFileUploadStatus(file, data, size, options, context);

      return {
        ...state,
        uploads: prepend ? [newUpload, ...state.uploads] : [...state.uploads, newUpload],
        uploadsCount: state.uploads.length + 1,
      };
    },

    [uploadActionTypes.UPLOAD_FILE_QUEUE_REMOVE]: (state, { payload: { file } }) => {
      const uploads = state.uploads.filter(upload => upload.file !== file);

      return {
        ...state,
        uploads,
        uploadsCount: getNotUploadedYet(uploads).length ? state.uploadsCount : 0,
      };
    },

    [uploadActionTypes.UPLOAD_FILE_START]: (state, { payload: { file, data, size, options, context } }) => {
      const upload = state.uploads.find(upload => upload.file === file);
      const uploads = upload
        ? state.uploads
        : [...state.uploads, createFileUploadStatus(file, data, size, options, context)];
      const updatedUploads = uploads.map(upload => {
        if (upload.file === file) {
          return {
            ...upload,
            isUploading: true,
            retryCount: upload.retryCount + 1,
          };
        }
        return upload;
      });

      return {
        ...state,
        interuptedWithNetworkError: false,
        uploads: updatedUploads,
        uploadsCount: getNotUploadedYet(updatedUploads).length ? state.uploadsCount : 0,
      };
    },

    [uploadActionTypes.UPLOAD_FILE_STOP]: (state, { payload: { file } }) => {
      const uploads = state.uploads.map(upload => {
        if (upload.file === file) {
          return {
            ...upload,
            isUploading: false,
          };
        }
        return upload;
      });

      return {
        ...state,
        uploads,
        uploadsCount: getNotUploadedYet(uploads).length ? state.uploadsCount : 0,
      };
    },

    [uploadActionTypes.UPLOAD_FILE_RETRY]: (state, { payload: { file } }) => {
      const upload = state.uploads.find(upload => upload.file === file);

      if (!upload) {
        return state;
      }
      const uploads = [upload, ..._without(uploads, upload)];
      const updatedUploads = uploads.map(upload => {
        if (upload.file === file) {
          return {
            ...upload,
            isUploading: true,
            retryCount: upload.retryCount + 1,
          };
        }
        return upload;
      });

      return {
        ...state,
        uploads: updatedUploads,
        uploadsCount: getNotUploadedYet(updatedUploads).length ? state.uploadsCount : 0,
      };
    },

    [uploadActionTypes.UPLOAD_FILE_PROGRESS]: (state, { payload: { file, progress } }) => {
      const uploads = state.uploads.map(upload => {
        if (upload.file === file) {
          return {
            ...upload,
            progress: (progress / upload.size) * 100,
          };
        }
        return upload;
      });

      return {
        ...state,
        uploads,
      };
    },

    [uploadActionTypes.UPLOAD_FILE_PROGRESS_WITH_UPLOAD_ID]: (state, { payload: { uploadId, progress } }) => {
      const uploads = state.uploads.map(upload => {
        if (upload.uploadId === uploadId) {
          return {
            ...upload,
            progress: (progress / upload.size) * 100,
          };
        }
        return upload;
      });

      return {
        ...state,
        uploads,
      };
    },

    [uploadActionTypes.UPLOAD_FILE_COMPLETE]: (state, { payload: { file } }) => {
      const uploads = state.uploads.map(upload => {
        if (upload.file === file) {
          return {
            ...upload,
            progress: 100,
            isUploading: false,
            isUploaded: true,
          };
        }
        return upload;
      });

      return {
        ...state,
        uploads,
        uploadsCount: getNotUploadedYet(uploads).length ? state.uploadsCount : 0,
      };
    },

    [uploadActionTypes.UPLOAD_FILE_ERROR]: (state, { payload: { file, error } }) => {
      const uploads = state.uploads.map(upload => {
        if (upload.file === file) {
          return {
            ...upload,
            progress: 0,
            isUploading: false,
            isUploaded: false,
            error,
          };
        }
        return upload;
      });

      return {
        ...state,
        uploads,
        uploadsCount: getNotUploadedYet(uploads).length ? state.uploadsCount : 0,
      };
    },

    [uploadActionTypes.UPLOAD_FILE_CANCEL]: (state, { payload: { file, error } }) => {
      const uploads = state.uploads.map(upload => {
        if (upload.file === file) {
          return {
            ...upload,
            progress: 0,
            isUploading: false,
            isUploaded: false,
            isCancelled: true,
            error,
          };
        }
        return upload;
      });

      return {
        ...state,
        uploads,
        uploadsCount: getNotUploadedYet(uploads).length ? state.uploadsCount : 0,
      };
    },
    [uploadActionTypes.UPLOAD_QUEUE_CLEAN]: (state, { payload: { cleanFailedUploads, cleanCancelledUploads } }) => {
      const uploads = state.uploads.filter(upload => {
        if (upload.isUploaded) {
          return false;
        }
        if (cleanFailedUploads && upload.error) {
          return false;
        }
        if (cleanCancelledUploads && upload.isCancelled) {
          return false;
        }
        return true;
      });

      return {
        ...state,
        uploads,
        uploadsCount: getNotUploadedYet(uploads).length ? state.uploadsCount : 0,
      };
    },
    [uploadActionTypes.UPLOAD_SET_ID]: (state, { payload: { file, uploadId } }) => {
      return {
        ...state,
        uploads: state.uploads.map(upload => {
          if (upload.file === file) {
            return {
              ...upload,
              uploadId,
            };
          }
          return upload;
        }),
      };
    },
    [uploadActionTypes.UPLOAD_INTERUPTED_NETWORK_ERROR]: state => ({
      ...state,
      interuptedWithNetworkError: true,
    }),
  };
  Object.values(reduxUploadsMap).forEach(uploadDescriptor => {
    uploadDescriptor.entities.forEach(entity => {
      reducers[entity.actionTypes.CLEAR_DATA] = (state, { payload: { context } }) => {
        const uploads =
          context && context.itemId
            ? state.uploads.filter(
                upload => upload.isUploading || !(upload.context && upload.context.itemId === context.itemId)
              )
            : state.uploads;
        return {
          ...state,
          uploads,
          uploadsCount: getNotUploadedYet(uploads).length ? state.uploadsCount : 0,
        };
      };
    });
  });
  return handleActions(reducers, { ...initialState });
}

function createUploadSelectors(options = null) {
  const selectUpload = state => state.x_upload;

  const getUploadByOptions = uploads => {
    if (options) {
      return uploads.filter(upload => upload.options === options);
    }
    return uploads;
  };

  return {
    getUploads: createSelector(selectUpload, uploadState => getUploadByOptions(uploadState.uploads)),
    getUploadsByContext: context =>
      createSelector(selectUpload, uploadState => {
        const allUploads = getUploadByOptions(uploadState.uploads);

        return _filter(allUploads, upload => _isMatch(upload.context, context));
      }),
    getFileToUpload: createSelector(selectUpload, uploadState =>
      getUploadByOptions(uploadState.uploads).find(
        upload => !upload.isUploading && !upload.isUploaded // && !upload.error && !upload.isCancelled
      )
    ),
    getPendingUpload: createSelector(selectUpload, uploadState =>
      getUploadByOptions(uploadState.uploads).find(upload => upload.isUploading)
    ),
    isUploading: createSelector(selectUpload, uploadState =>
      getUploadByOptions(uploadState.uploads).some(upload => upload.isUploading)
    ),
    getOverallProgress: createSelector(selectUpload, ({ uploads, uploadsCount }) => {
      const optionUploads = getUploadByOptions(uploads);

      const overallProgress = optionUploads.reduce((acc, upload) => {
        return acc + (upload.isUploaded ? 100 : upload.progress);
      }, 0);

      return (overallProgress + (uploadsCount - optionUploads.length) * 100) / uploadsCount;
    }),
    isUploadInteruptedByNetworkError: createSelector(
      selectUpload,
      uploadState => uploadState.interuptedWithNetworkError
    ),
  };
}

export const uploadSelectors = createUploadSelectors();

export function createUploadDescriptor(name, options, entities) {
  let uploadDescriptor = reduxUploadsMap[name];
  if (global.isReduxSetup && !uploadDescriptor) {
    throw new Error('Redux is already setup. Uploads must be registered earlier. Upload "' + name + '" is ignored.');
  }
  if (!uploadDescriptor) {
    uploadDescriptor = createActions(options);
    uploadDescriptor.name = name;
    uploadDescriptor.selectors = createUploadSelectors(options);
    uploadDescriptor.logger = getLogger(name);
    uploadDescriptor.entities = entities;
    reduxUploadsMap[name] = uploadDescriptor;
  }
  return uploadDescriptor;
}

function createFileUploadStatus(file, args, size, options, context) {
  return {
    context,
    uploadId: null,
    file,
    options,
    uploadLink: options.url,
    args,
    thumbnail: null,
    isUploading: false,
    isUploaded: false,
    progress: 0,
    error: null,
    isCancelled: false,
    retryCount: 0,
    size,
  };
}

function getNotUploadedYet(uploads) {
  return uploads.filter(upload => !upload.isUploaded && !upload.error && !upload.isCancelled);
}
